/**
 * Offline workout sync: owns the replay of queued set saves and the upload of
 * queued form videos, and publishes the counts the indicator shows.
 *
 * Runs when the connection returns (native @capacitor/network via
 * connectivity.js, or the browser's online event), when the page becomes
 * visible, every 30s while anything is pending, and once on boot (a queue left
 * by a previous session, e.g. the app was killed at the gym).
 *
 * One sync at a time per page, and per device when the Web Locks API exists,
 * so two tabs never drain the same queue at once. If they somehow did, the
 * idempotency keys make that a wasted request, not a duplicate.
 *
 * Sets go first, then videos: a coach opening the workout should see the
 * numbers before the clips, and the set saves are tiny.
 *
 * Inert with the flag off.
 */

import axios from "axios";
import Honeybadger from "@honeybadger-io/js";
import { isOfflineLoggingEnabled } from "./offline_flag";
import { isOnline, onConnectivityChange } from "./connectivity";
import { isOfflineDbAvailable } from "./offline_db";
import {
  installOfflineInterceptors,
  IDEMPOTENCY_HEADER,
} from "./axios_offline";
import {
  MAX_ATTEMPTS,
  STATUS_FAILED,
  STATUS_PENDING,
  discardSetSave,
  hasAnyPending,
  loadSetSaves,
  markSetSave,
  removeSetSave,
} from "./set_save_queue";
import {
  MAX_VIDEO_ATTEMPTS,
  VIDEO_STATUS_FAILED,
  VIDEO_STATUS_PENDING,
  fileFromRecord,
  loadOfflineVideos,
  removeOfflineVideo,
  saveOfflineVideo,
  updateOfflineVideo,
  withoutBlob,
} from "./offline_video_queue";
import { reportDroppedActualSets } from "../utils/report_dropped_actual_sets";

export const OFFLINE_VIDEO_UPLOADED_EVENT = "tkc:offline-video-uploaded";
const RETRY_INTERVAL_MS = 30 * 1000;
const LOCK_NAME = "tkc-offline-sync";

const currentUserId = () =>
  window._tkcCurrentUserId != null ? String(window._tkcCurrentUserId) : null;

// ---- status ---------------------------------------------------------------

const initialStatus = () => ({
  online: true,
  pendingSets: 0,
  pendingVideos: 0,
  failedSets: 0,
  failedVideos: 0,
  syncing: false,
  videoProgress: null, // 0..100 while a queued video is uploading
  justSynced: false,
});

let status = initialStatus();
const listeners = new Set();

const publish = (patch) => {
  status = { ...status, ...patch };
  listeners.forEach((listener) => {
    try {
      listener(status);
    } catch (error) {
      reportError(error, "listener");
    }
  });
};

export const getOfflineSyncStatus = () => status;

export const subscribeOfflineSyncStatus = (listener) => {
  listeners.add(listener);
  listener(status);
  return () => listeners.delete(listener);
};

/** True while anything is queued for this user. Read synchronously by
 *  capacitor/offline.js so its reconnect reload does not race the sync. */
export const hasPendingOfflineWork = () =>
  hasAnyPending() || status.pendingVideos > 0 || status.syncing;

function reportError(error, stage, context = {}) {
  try {
    Honeybadger.notify(error, {
      context: { component: "offline_sync", stage, ...context },
    });
  } catch {
    // reporting must never break the app
  }
}

export async function refreshOfflineCounts() {
  if (!isOfflineDbAvailable()) return;
  const userId = currentUserId();
  try {
    const [sets, videos] = await Promise.all([
      loadSetSaves(),
      loadOfflineVideos(),
    ]);
    const mine = (records) => records.filter((r) => r.userId === userId);
    publish({
      online: isOnline(),
      pendingSets: mine(sets).filter((r) => r.status === STATUS_PENDING).length,
      failedSets: mine(sets).filter((r) => r.status === STATUS_FAILED).length,
      pendingVideos: mine(videos).filter(
        (r) => r.status === VIDEO_STATUS_PENDING,
      ).length,
      failedVideos: mine(videos).filter((r) => r.status === VIDEO_STATUS_FAILED)
        .length,
    });
  } catch (error) {
    reportError(error, "refresh_counts");
  }
}

// ---- set saves --------------------------------------------------------------

// What to do after a replay failed. "stop" ends this pass (retry later);
// "skip" moves on to the next entry.
const classifySetFailure = (error) => {
  const response = error?.response;
  if (!response) return "offline";
  const { status: code } = response;
  // Still running from an earlier delivery, or the session changed account
  // (axios_setup reloads for that one). Either way, try again later.
  if (code === 409 || code === 401) return "later";
  if (code >= 500 || code === 429) return "retry";
  return "permanent"; // 403, 404, 422: the server will never accept it
};

// Bounds one pass. Each step either removes an entry, marks it failed, or ends
// the pass, so this is only reached if saves keep arriving faster than they
// replay; the next trigger carries on.
const MAX_SET_STEPS_PER_PASS = 500;

async function flushSetSaves() {
  const userId = currentUserId();

  // Re-read the head of the queue every step rather than walking a snapshot. A
  // save made while its predecessor was in flight supersedes that entry in
  // place (same id, new key, newer data), and removeSetSave then keeps it, so
  // re-reading is what sends the newer data in this same pass. Saves queued
  // during the pass are picked up the same way.
  for (let step = 0; step < MAX_SET_STEPS_PER_PASS; step += 1) {
    const entry = (await loadSetSaves()).find(
      (candidate) =>
        candidate.userId === userId && candidate.status === STATUS_PENDING,
    );
    if (!entry) return "done";
    if (!isOnline()) return "offline";
    try {
      const response = await axios.put(entry.url, entry.data, {
        headers: { [IDEMPOTENCY_HEADER]: entry.idempotencyKey },
        tkcOfflineReplay: true,
      });
      await removeSetSave(entry);
      reportDroppedActualSets(response, {
        assignedSetId: entry.assignedSetId,
        phase: "offline-replay",
      });
    } catch (error) {
      const outcome = classifySetFailure(error);
      if (outcome === "offline" || outcome === "later") return outcome;
      const attempts = (entry.attempts || 0) + 1;
      const giveUp = outcome === "permanent" || attempts >= MAX_ATTEMPTS;
      await markSetSave(entry, {
        attempts,
        lastError: error?.response?.status ?? String(error?.message || error),
        status: giveUp ? STATUS_FAILED : STATUS_PENDING,
      });
      if (giveUp) {
        reportError(error, "set_replay_failed", {
          assignedSetId: entry.assignedSetId,
          status: error?.response?.status,
          attempts,
        });
        continue; // a dead entry must not block the sets queued behind it
      }
      return "retry";
    }
  }
  return "more";
}

// ---- videos -------------------------------------------------------------

// uploadVideo() reports through a snackbar; a background drain has no page
// snackbar, and the indicator is the feedback, so its messages are dropped.
const silentSnackBar = { notify: () => {}, error: () => {}, success: () => {} };

const uploadRecord = async (record) => {
  // Loaded on demand: the uploader is large and only needed when a video is
  // actually waiting.
  const { uploadVideo } = await import(
    "../components/shared/videos/upload_video"
  );
  return new Promise((resolve) => {
    try {
      uploadVideo({
        file: fileFromRecord(record),
        filename: record.uploadFilename,
        assignedSetId: record.assignedSetId,
        setIndex: record.setIndex,
        assignedExerciseId: record.assignedExerciseId,
        workoutId: record.workoutId,
        clientUploadKey: record.clientUploadKey,
        snackBar: silentSnackBar,
        setProgress: (value) => {
          if (value != null) publish({ videoProgress: Math.round(value) });
        },
        onUploadSuccess: (response) => resolve({ ok: true, response }),
        onUploadFailure: (error) => resolve({ ok: false, error }),
      });
    } catch (error) {
      resolve({ ok: false, error });
    }
  });
};

async function drainVideos() {
  const userId = currentUserId();
  const records = (await loadOfflineVideos()).filter(
    (record) =>
      record.userId === userId && record.status === VIDEO_STATUS_PENDING,
  );

  for (const record of records) {
    if (!isOnline()) return "offline";
    publish({ videoProgress: 0 });
    const result = await uploadRecord(record);
    publish({ videoProgress: null });

    if (result.ok) {
      await removeOfflineVideo(record.id);
      window.dispatchEvent(
        new CustomEvent(OFFLINE_VIDEO_UPLOADED_EVENT, {
          detail: {
            record: withoutBlob(record),
            video: result.response?.data ?? null,
          },
        }),
      );
      continue;
    }
    if (!isOnline()) return "offline"; // signal died mid-upload; keep it
    const attempts = (record.attempts || 0) + 1;
    const giveUp = attempts >= MAX_VIDEO_ATTEMPTS;
    await updateOfflineVideo(record, {
      attempts,
      lastError: String(result.error?.message || result.error || "failed"),
      status: giveUp ? VIDEO_STATUS_FAILED : VIDEO_STATUS_PENDING,
    });
    if (giveUp) {
      reportError(
        result.error || new Error("offline video failed"),
        "video_upload_failed",
        {
          attempts,
        },
      );
      continue;
    }
    return "retry";
  }
  return "done";
}

// ---- orchestration --------------------------------------------------------

let syncing = null;

const withDeviceLock = (run) => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : null;
  if (!locks?.request) return run();
  // ifAvailable: another tab is already syncing, so this one has nothing to do.
  return locks.request(LOCK_NAME, { ifAvailable: true }, (lock) =>
    lock ? run() : "busy",
  );
};

/** Sync now if there is a connection. Single-flight; resolves when done. */
export function syncOfflineWork() {
  if (!isOfflineLoggingEnabled() || !isOfflineDbAvailable()) {
    return Promise.resolve("disabled");
  }
  if (syncing) return syncing;
  if (!isOnline()) {
    refreshOfflineCounts();
    return Promise.resolve("offline");
  }

  syncing = (async () => {
    // Fresh counts first: "All synced" is only shown when there was something
    // to sync, and the published counts can lag an enqueue.
    await refreshOfflineCounts();
    const hadWork = status.pendingSets + status.pendingVideos > 0;
    publish({ syncing: true, justSynced: false });
    try {
      return await withDeviceLock(async () => {
        const setsOutcome = await flushSetSaves();
        await refreshOfflineCounts();
        if (setsOutcome !== "done") return setsOutcome;
        const videosOutcome = await drainVideos();
        return videosOutcome;
      });
    } catch (error) {
      reportError(error, "sync");
      return "error";
    } finally {
      syncing = null;
      await refreshOfflineCounts();
      const clear =
        status.pendingSets + status.pendingVideos === 0 &&
        status.failedSets + status.failedVideos === 0;
      publish({ syncing: false, justSynced: hadWork && clear });
    }
  })();
  return syncing;
}

/**
 * VideoUploadQueue's hand-off: keep this video on the phone and upload it when
 * the connection returns. Rejects (OfflineStorageFullError, or IndexedDB
 * unavailable) when it cannot be kept, so the caller can fail it visibly.
 */
export async function keepVideoForLater(item) {
  const saved = await saveOfflineVideo(item);
  await refreshOfflineCounts();
  return saved;
}

export async function retryFailedOfflineWork() {
  const userId = currentUserId();
  const [sets, videos] = await Promise.all([
    loadSetSaves(),
    loadOfflineVideos(),
  ]);
  await Promise.all([
    ...sets
      .filter((r) => r.userId === userId && r.status === STATUS_FAILED)
      .map((r) => markSetSave(r, { status: STATUS_PENDING, attempts: 0 })),
    ...videos
      .filter((r) => r.userId === userId && r.status === VIDEO_STATUS_FAILED)
      .map((r) =>
        updateOfflineVideo(r, { status: VIDEO_STATUS_PENDING, attempts: 0 }),
      ),
  ]);
  await refreshOfflineCounts();
  return syncOfflineWork();
}

/** Throw away what the server keeps refusing. Only failed items, only this user's. */
export async function discardFailedOfflineWork() {
  const userId = currentUserId();
  const [sets, videos] = await Promise.all([
    loadSetSaves(),
    loadOfflineVideos(),
  ]);
  await Promise.all([
    ...sets
      .filter((r) => r.userId === userId && r.status === STATUS_FAILED)
      .map((r) => discardSetSave(r.id)),
    ...videos
      .filter((r) => r.userId === userId && r.status === VIDEO_STATUS_FAILED)
      .map((r) => removeOfflineVideo(r.id)),
  ]);
  await refreshOfflineCounts();
}

let started = false;

/** Boot hook (packs/app.js). No-op with the flag off; runs once. */
export function initOfflineSync({ mountIndicator } = {}) {
  if (started || !isOfflineLoggingEnabled() || !isOfflineDbAvailable()) {
    return false;
  }
  started = true;

  installOfflineInterceptors(axios, {
    onQueued: () => {
      refreshOfflineCounts().then(() => {
        if (isOnline()) syncOfflineWork();
      });
    },
  });

  onConnectivityChange((online) => {
    publish({ online });
    if (online) syncOfflineWork();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") syncOfflineWork();
  });

  setInterval(() => {
    const waiting = status.pendingSets + status.pendingVideos > 0;
    if (waiting && isOnline()) syncOfflineWork();
  }, RETRY_INTERVAL_MS);

  mountIndicator?.();
  publish({ online: isOnline() });
  refreshOfflineCounts().then(() => syncOfflineWork());
  return true;
}

export const resetOfflineSyncForTests = () => {
  started = false;
  syncing = null;
  status = initialStatus();
  listeners.clear();
};
